import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { Mail, Lock, LogIn, ChefHat, Eye, EyeOff, KeyRound, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLoginMutation, useVerify2FAMutation, useResend2FAMutation, useGetProfileQuery } from "@/store";
import { toast } from "sonner";

interface LoginFormData {
  email: string;
  password: string;
}

interface TwoFactorState {
  userId: string;
  email: string;
}

export function AdminLoginScreen() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorState, setTwoFactorState] = useState<TwoFactorState | null>(null);
  const [otpValue, setOtpValue] = useState<string[]>(["", "", "", "", "", ""]);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [login, { isLoading: isLoginLoading }] = useLoginMutation();
  const [verify2FA, { isLoading: isVerifying }] = useVerify2FAMutation();
  const [resend2FA, { isLoading: isResending }] = useResend2FAMutation();

  // Check if user is already authenticated (cookie-based)
  const { data: profileData, isLoading: isCheckingAuth } = useGetProfileQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });

  // Redirect if already logged in as admin/staff
  useEffect(() => {
    if (profileData?.data) {
      const userRole = profileData.data.role?.toUpperCase();
      if (userRole === "ADMIN" || userRole === "STAFF" || userRole === "SUPER_ADMIN") {
        navigate("/admin", { replace: true });
      }
    }
  }, [profileData, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-sm text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  const onSubmit = async (data: LoginFormData) => {
    try {
      const result = await login(data).unwrap();

      if (result.success && result.data) {
        // Check if 2FA is required
        if ("requires2FA" in result.data && result.data.requires2FA) {
          setTwoFactorState({
            userId: result.data.userId,
            email: result.data.email,
          });
          toast.success("Verification code sent to your email");
          setResendCooldown(60); // Start 60 second cooldown
          return;
        }

        // Regular login (for customers, though they shouldn't be here)
        if ("user" in result.data) {
          const user = result.data.user;
          const userRole = user.role?.toUpperCase();
          if (userRole !== "ADMIN" && userRole !== "STAFF" && userRole !== "SUPER_ADMIN") {
            toast.error("Access denied. Admin access required.");
            return;
          }

          toast.success("Login successful! Welcome back.");
          navigate("/admin", { replace: true });
        }
      }
    } catch (error: any) {
      const errorMessage = error?.data?.error || error?.data?.message || "Login failed. Please try again.";
      toast.error(errorMessage);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otpValue];
    newOtp[index] = value;
    setOtpValue(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace - move to previous input
    if (e.key === "Backspace" && !otpValue[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otpValue];
    pastedData.split("").forEach((char, i) => {
      if (i < 6) newOtp[i] = char;
    });
    setOtpValue(newOtp);

    // Focus the last filled input or the next empty one
    const lastFilledIndex = Math.min(pastedData.length - 1, 5);
    otpInputRefs.current[lastFilledIndex]?.focus();
  };

  const handleVerify2FA = async () => {
    if (!twoFactorState) return;

    const otp = otpValue.join("");
    if (otp.length !== 6) {
      toast.error("Please enter the complete 6-digit code");
      return;
    }

    try {
      const result = await verify2FA({
        userId: twoFactorState.userId,
        otp,
      }).unwrap();

      if (result.success && result.data?.user) {
        const user = result.data.user;
        const userRole = user.role?.toUpperCase();

        if (userRole !== "ADMIN" && userRole !== "STAFF" && userRole !== "SUPER_ADMIN") {
          toast.error("Access denied. Admin access required.");
          return;
        }

        toast.success("Login successful! Welcome back.");
        navigate("/admin", { replace: true });
      }
    } catch (error: any) {
      const errorMessage = error?.data?.error || error?.data?.message || "Invalid verification code";
      toast.error(errorMessage);
      // Clear the OTP inputs on error
      setOtpValue(["", "", "", "", "", ""]);
      otpInputRefs.current[0]?.focus();
    }
  };

  const handleResend2FA = async () => {
    if (!twoFactorState || resendCooldown > 0) return;

    try {
      const result = await resend2FA({ userId: twoFactorState.userId }).unwrap();
      if (result.success) {
        toast.success("New verification code sent to your email");
        setResendCooldown(60);
        setOtpValue(["", "", "", "", "", ""]);
        otpInputRefs.current[0]?.focus();
      }
    } catch (error: any) {
      const errorMessage = error?.data?.error || error?.data?.message || "Failed to resend code";
      toast.error(errorMessage);
    }
  };

  const handleBackToLogin = () => {
    setTwoFactorState(null);
    setOtpValue(["", "", "", "", "", ""]);
  };

  // 2FA Verification Screen
  if (twoFactorState) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 px-4 py-12">
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="bg-card/50 backdrop-blur-xl border-border/50 shadow-xl">
              <CardHeader className="space-y-1 pb-4">
                <div className="flex items-center justify-center mb-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <KeyRound className="h-8 w-8 text-primary" />
                  </div>
                </div>
                <CardTitle className="text-2xl font-bold text-center">
                  Two-Factor Authentication
                </CardTitle>
                <CardDescription className="text-center">
                  Enter the 6-digit code sent to <br />
                  <span className="font-medium text-foreground">{twoFactorState.email}</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* OTP Input */}
                <div className="flex justify-center gap-2">
                  {otpValue.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(el) => { otpInputRefs.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={index === 0 ? handleOtpPaste : undefined}
                      className="w-12 h-14 text-center text-2xl font-bold rounded-xl border-border/50 bg-background/50 backdrop-blur-sm focus-visible:ring-2 focus-visible:ring-primary/20"
                      autoFocus={index === 0}
                    />
                  ))}
                </div>

                {/* Verify Button */}
                <Button
                  onClick={handleVerify2FA}
                  className="w-full h-11 rounded-xl shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-r from-primary to-primary/80"
                  disabled={isVerifying || otpValue.join("").length !== 6}
                >
                  {isVerifying ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="mr-2"
                      >
                        <ChefHat className="h-4 w-4" />
                      </motion.div>
                      Verifying...
                    </>
                  ) : (
                    <>
                      <LogIn className="mr-2 h-4 w-4" />
                      Verify & Sign In
                    </>
                  )}
                </Button>

                {/* Resend Code */}
                <div className="text-center space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Didn't receive the code?
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleResend2FA}
                    disabled={isResending || resendCooldown > 0}
                    className="text-primary hover:text-primary/80"
                  >
                    {isResending ? (
                      "Sending..."
                    ) : resendCooldown > 0 ? (
                      `Resend code in ${resendCooldown}s`
                    ) : (
                      "Resend Code"
                    )}
                  </Button>
                </div>

                {/* Back to Login */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBackToLogin}
                  className="w-full rounded-xl"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Login
                </Button>

                {/* Security Note */}
                <p className="text-xs text-muted-foreground text-center">
                  The code expires in 5 minutes. Check your spam folder if you don't see it.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // Regular Login Screen
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 px-4 py-12">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Login Card */}
          <Card className="bg-card/50 backdrop-blur-xl border-border/50 shadow-xl">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold text-center">
                Welcome Back
              </CardTitle>
              <CardDescription className="text-center">
                Sign in to access the admin dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Email Field */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email Address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="admin@zestandzeal.com.ng"
                      className="pl-10 h-11 rounded-xl border-border/50 bg-background/50 backdrop-blur-sm focus-visible:ring-2 focus-visible:ring-primary/20"
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Invalid email address",
                        },
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      className="pl-10 pr-10 h-11 rounded-xl border-border/50 bg-background/50 backdrop-blur-sm focus-visible:ring-2 focus-visible:ring-primary/20"
                      {...register("password", {
                        required: "Password is required",
                        minLength: {
                          value: 6,
                          message: "Password must be at least 6 characters",
                        },
                      })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-border"
                    />
                    <span className="text-muted-foreground">Remember me</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => navigate("/admin/forgot-password")}
                    className="text-primary hover:underline font-medium"
                  >
                    Forgot password?
                  </button>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full h-11 rounded-xl shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-r from-primary to-primary/80"
                  disabled={isLoginLoading}
                >
                  {isLoginLoading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="mr-2"
                      >
                        <ChefHat className="h-4 w-4" />
                      </motion.div>
                      Signing in...
                    </>
                  ) : (
                    <>
                      <LogIn className="mr-2 h-4 w-4" />
                      Sign In
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mt-6 text-center text-sm text-muted-foreground"
          >
            <p>
              Need help?{" "}
              <a href="/contact" className="text-primary hover:underline font-medium">
                Contact Support
              </a>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
